import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Task, TaskPriority, Sprint, TaskType } from '../../types';
import { getProjectTasks, updateTaskSortOrder, updateTaskStoryPoints } from '../../services/taskService';
import { getProjectSprints, moveTaskToSprint, SprintStatus } from '../../services/sprintService';
import TaskDetailDrawer from '../../task/TaskDetailDrawer';
import NewTaskModal from '../../task/NewTaskModal';

interface Props { pca: IPublicClientApplication; projectId: string; refreshKey?: number }

const PRIORITY: Record<number, { bg: string; fg: string; label: string }> = {
  [TaskPriority.Critical]: { bg: '#DC2626', fg: '#fff', label: 'Critical' },
  [TaskPriority.High]:     { bg: '#E59F1C', fg: '#1C1917', label: 'High' },
  [TaskPriority.Medium]:   { bg: '#2563EB', fg: '#fff', label: 'Medium' },
  [TaskPriority.Low]:      { bg: '#6B7280', fg: '#fff', label: 'Low' },
};

function initials(name?: string | null): string {
  if (!name) return '';
  const p = name.trim().split(/\s+/).filter(Boolean);
  return p.length === 0 ? '' : (p.length === 1 ? p[0][0] : p[0][0] + p[p.length - 1][0]).toUpperCase();
}

export default function BacklogView({ pca, projectId, refreshKey }: Props) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Task | null>(null);
  const [showNew, setShowNew] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([getProjectTasks(pca, projectId), getProjectSprints(pca, projectId)]);
      // Backlog = tasks with no sprint, ordered by sortorder (manual backlog order).
      const backlog = t
        .filter(x => !x._dagrun_pm_sprintid_value)
        .sort((a, b) => (a.dagrun_pm_sortorder ?? 0) - (b.dagrun_pm_sortorder ?? 0));
      setTasks(backlog); setSprints(s);
    } finally { setLoading(false); }
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload, refreshKey]);

  const openSprints = sprints.filter(s => s.dagrun_pm_status !== SprintStatus.Completed && s.dagrun_pm_status !== SprintStatus.Archived);

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;
    const next = [...tasks];
    const [moved] = next.splice(source.index, 1);
    next.splice(destination.index, 0, moved);
    const renumbered = next.map((t, i) => ({ ...t, dagrun_pm_sortorder: i }));
    setTasks(renumbered);
    const changed = renumbered.filter(t => {
      const prev = tasks.find(x => x.dagrun_pm_taskid === t.dagrun_pm_taskid);
      return (prev?.dagrun_pm_sortorder ?? null) !== t.dagrun_pm_sortorder;
    });
    try { await Promise.all(changed.map(t => updateTaskSortOrder(pca, t.dagrun_pm_taskid, t.dagrun_pm_sortorder ?? 0))); }
    catch { reload(); }
  };

  const setPoints = async (task: Task, points: number | null) => {
    setTasks(prev => prev.map(t => t.dagrun_pm_taskid === task.dagrun_pm_taskid ? { ...t, dagrun_pm_storypoints: points } : t));
    try { await updateTaskStoryPoints(pca, task.dagrun_pm_taskid, points); } catch { reload(); }
  };

  const addToSprint = async (task: Task, sprintId: string) => {
    setTasks(prev => prev.filter(t => t.dagrun_pm_taskid !== task.dagrun_pm_taskid));
    try { await moveTaskToSprint(pca, task.dagrun_pm_taskid, sprintId); } catch { reload(); }
  };

  if (loading) return <div className="spinner" style={{ margin: '60px auto' }} />;

  return (
    <div style={{ padding: '16px 24px', height: '100%', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>Backlog ({tasks.length})</h3>
        <button type="button" className="btn-primary" onClick={() => setShowNew(true)}>+ Add to Backlog</button>
      </div>

      {tasks.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-4)', fontSize: 14 }}>
          Backlog is empty — add tasks to start planning
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="backlog">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {tasks.map((t, index) => {
                  const prio = t.dagrun_pm_priority != null ? PRIORITY[t.dagrun_pm_priority] : null;
                  return (
                    <Draggable key={t.dagrun_pm_taskid} draggableId={t.dagrun_pm_taskid} index={index}>
                      {(dp, snap) => (
                        <div ref={dp.innerRef} {...dp.draggableProps}
                          style={{
                            ...dp.draggableProps.style,
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', marginBottom: 6,
                            border: '1px solid var(--border-light)', borderRadius: 7, background: snap.isDragging ? 'var(--surface-2)' : '#fff',
                            boxShadow: snap.isDragging ? 'var(--shadow-panel)' : 'none',
                          }}>
                          <span {...dp.dragHandleProps} title="Drag to reorder"
                            style={{ cursor: 'grab', color: 'var(--ink-5)', fontSize: 14, userSelect: 'none' }}>⋮⋮</span>
                          {prio
                            ? <span style={{ background: prio.bg, color: prio.fg, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 3, flexShrink: 0 }}>{prio.label}</span>
                            : <span style={{ width: 4 }} />}
                          <button type="button" onClick={() => setSelected(t)}
                            style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-ui)' }}>
                            {t.dagrun_pm_name}
                          </button>
                          <input type="number" min={0} title="Story points"
                            value={t.dagrun_pm_storypoints ?? ''}
                            onChange={e => setTasks(prev => prev.map(x => x.dagrun_pm_taskid === t.dagrun_pm_taskid ? { ...x, dagrun_pm_storypoints: e.target.value === '' ? null : Number(e.target.value) } : x))}
                            onBlur={e => setPoints(t, e.target.value === '' ? null : Math.max(0, Number(e.target.value)))}
                            style={{ width: 48, fontSize: 12, padding: '3px 6px', textAlign: 'center', border: '1px solid var(--border-light)', borderRadius: 4 }} />
                          {t.dagrun_pm_assigneeid && (
                            <span title={t.dagrun_pm_assigneename ?? ''} style={{ width: 24, height: 24, borderRadius: '50%', background: '#0E7490', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {initials(t.dagrun_pm_assigneename)}
                            </span>
                          )}
                          <select defaultValue="" onChange={e => { if (e.target.value) addToSprint(t, e.target.value); }}
                            disabled={openSprints.length === 0}
                            title={openSprints.length === 0 ? 'No open sprints — create one in Settings' : 'Add to sprint'}
                            style={{ fontSize: 11, padding: '3px 6px', border: '1px solid #0E7490', borderRadius: 4, color: '#0E7490', background: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                            <option value="">+ Add to Sprint</option>
                            {openSprints.map(s => <option key={s.dagrun_pm_sprintid} value={s.dagrun_pm_sprintid}>{s.dagrun_pm_name}</option>)}
                          </select>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <TaskDetailDrawer task={selected} pca={pca} onClose={() => setSelected(null)} onSaved={() => reload()} />

      {showNew && (
        <NewTaskModal pca={pca} projectId={projectId} agile initialType={TaskType.Story}
          onCreated={() => { setShowNew(false); reload(); }}
          onCancel={() => setShowNew(false)} />
      )}
    </div>
  );
}
